/* ============================================================================
   APP CONFIG — resolved from Vite env (VITE_*) with mock-first defaults.

   `mode` selects the data plane:
     - 'mock'   : everything from the bundled mock store (no backend needed)
     - 'hybrid' : real GraphQL via the BFF where it exists, mock for futures
   `authMode` selects how a user signs in:
     - 'demo'     : the prototype identity picker (no backend) — DEFAULT
     - 'firebase' : Option B — per-user Firebase phone sign-in → real QA session
                    via the BFF. Active only when VITE_AUTH_MODE=firebase AND a
                    Firebase web config is present. See documentation/plan/07.
   ============================================================================ */
export type DataMode = 'mock' | 'hybrid';
export type AuthMode = 'demo' | 'firebase';

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  messagingSenderId?: string;
}

export interface AppConfig {
  runtimeTarget: 'web';
  mode: DataMode;
  authMode: AuthMode;
  /** Same-origin Pages Function (BFF) base path. */
  bffPath: string;
  /** Logical environment label (dev/qa/prod). */
  env: string;
  /** Firebase web config (public) for Option B phone auth, or null. */
  firebase: FirebaseWebConfig | null;
  /** True when this build allows the `?demo=1` persona-picker toggle (staging/dev). */
  demoToggleAllowed: boolean;
  /** True when the demo toggle is currently active (forces demo/mock at runtime). */
  demoOverride: boolean;
}

const env = import.meta.env;

/* -------------------------------------------------------------------------- */
/* Demo toggle (staging only) — let a reviewer flip a live firebase build into */
/* the prototype persona picker (Super-admin / Org / Dept / Host, mock data)   */
/* to review all role views, WITHOUT touching production. Opt-in two ways:     */
/*   • build:    VITE_ALLOW_DEMO=1 (set only by the staging workflow)          */
/*   • runtime:  ?demo=1 to enter (persisted), ?live=1 to exit.                */
/* Production never sets VITE_ALLOW_DEMO, so ?demo is a no-op there.           */
/* -------------------------------------------------------------------------- */
const DEMO_LS = 'yperea_force_demo';

/** Whether this build exposes the demo toggle at all. */
export const demoToggleAllowed: boolean = env.VITE_ALLOW_DEMO === '1' || !!env.DEV;

function readDemoOverride(): boolean {
  if (typeof window === 'undefined' || !demoToggleAllowed) return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('live')) {
      localStorage.removeItem(DEMO_LS);
    } else if (params.has('demo')) {
      const v = params.get('demo');
      if (v === null || v === '' || v === '1' || v === 'true') localStorage.setItem(DEMO_LS, '1');
      else localStorage.removeItem(DEMO_LS);
    }
    return localStorage.getItem(DEMO_LS) === '1';
  } catch {
    return false;
  }
}

const demoOverride = readDemoOverride();

/** Enter demo persona mode (persisted) and reload so config re-resolves. */
export function enterDemoMode(): void {
  try {
    localStorage.setItem(DEMO_LS, '1');
  } catch {
    /* ignore */
  }
  window.location.assign(window.location.pathname + '?demo=1');
}

/** Leave demo persona mode and return to the live sign-in. */
export function exitDemoMode(): void {
  try {
    localStorage.removeItem(DEMO_LS);
  } catch {
    /* ignore */
  }
  window.location.assign(window.location.pathname + '?live=1');
}

function readFirebase(): FirebaseWebConfig | null {
  const apiKey = env.VITE_FIREBASE_API_KEY;
  if (!apiKey) return null;
  return {
    apiKey,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: env.VITE_FIREBASE_PROJECT_ID ?? '',
    appId: env.VITE_FIREBASE_APP_ID ?? '',
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  };
}

const firebase = readFirebase();
// Firebase auth requires BOTH the opt-in flag and a usable config; otherwise fall
// back to demo. The staging demo toggle (?demo=1) also forces demo at runtime.
const authMode: AuthMode =
  !demoOverride && env.VITE_AUTH_MODE === 'firebase' && firebase ? 'firebase' : 'demo';

export const CONFIG: AppConfig = {
  runtimeTarget: 'web',
  // Demo mode always uses the bundled mock store (the personas live there).
  mode: demoOverride ? 'mock' : (env.VITE_DATA_MODE as DataMode) || 'mock',
  authMode,
  bffPath: env.VITE_BFF_PATH || '/api',
  env: env.VITE_ENV || 'dev',
  firebase,
  demoToggleAllowed,
  demoOverride,
};
